import OpportunitiesAPI from '../../api/opportunities';

const state = {
  records: [],
  kanbanData: null,
  currentOpportunity: null,
  uiFlags: {
    isFetching: false,
    isFetchingKanban: false,
    isFetchingDetail: false,
    isCreating: false,
    isMoving: false,
    isCreatingActivity: false,
  },
  meta: { totalCount: 0 },
};

const getters = {
  getOpportunities: $state => $state.records,
  getKanbanData: $state => $state.kanbanData,
  getUIFlags: $state => $state.uiFlags,
  getMeta: $state => $state.meta,
  getOpportunityById: $state => id =>
    $state.records.find(o => o.id === Number(id)),
  getCurrentOpportunity: $state => $state.currentOpportunity,
};

const mutations = {
  SET_OPPORTUNITIES($state, { records, meta }) {
    $state.records = records;
    $state.meta = meta || { totalCount: 0 };
  },
  SET_KANBAN_DATA($state, data) {
    $state.kanbanData = data;
  },
  ADD_OPPORTUNITY($state, opp) {
    $state.records.unshift(opp);
  },
  UPDATE_OPPORTUNITY($state, opp) {
    const index = $state.records.findIndex(o => o.id === opp.id);
    if (index !== -1) $state.records.splice(index, 1, opp);
  },
  DELETE_OPPORTUNITY($state, id) {
    $state.records = $state.records.filter(o => o.id !== id);
  },
  MOVE_OPPORTUNITY_STAGE($state, { id, pipeline_stage_id, kanban_order }) {
    // Optimistic update in kanban data
    if (!$state.kanbanData?.groups) return;
    let movedItem = null;

    $state.kanbanData.groups.forEach(group => {
      const idx = group.items.findIndex(item => item.id === id);
      if (idx !== -1) {
        movedItem = group.items.splice(idx, 1)[0];
        group.total -= 1;
      }
    });

    if (movedItem) {
      const targetGroup = $state.kanbanData.groups.find(
        g => g.id === pipeline_stage_id
      );
      if (targetGroup) {
        movedItem.kanban_order = kanban_order;
        targetGroup.items.push(movedItem);
        targetGroup.total += 1;
      }
    }
  },
  SET_CURRENT_OPPORTUNITY($state, opp) {
    $state.currentOpportunity = opp;
  },
  ADD_ACTIVITY($state, activity) {
    if ($state.currentOpportunity?.activities) {
      $state.currentOpportunity.activities.unshift(activity);
    }
  },
  REMOVE_ACTIVITY($state, activityId) {
    if ($state.currentOpportunity?.activities) {
      $state.currentOpportunity.activities =
        $state.currentOpportunity.activities.filter(a => a.id !== activityId);
    }
  },
  SET_UI_FLAG($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
};

const actions = {
  async fetch({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await OpportunitiesAPI.get(params);
      commit('SET_OPPORTUNITIES', {
        records: data.payload,
        meta: data.meta,
      });
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  async fetchKanban({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetchingKanban: true });
    try {
      const { data } = await OpportunitiesAPI.kanban(params);
      commit('SET_KANBAN_DATA', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingKanban: false });
    }
  },

  async create({ commit }, opportunityData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await OpportunitiesAPI.create(opportunityData);
      commit('ADD_OPPORTUNITY', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async update({ commit }, { id, ...updateData }) {
    try {
      const { data } = await OpportunitiesAPI.update(id, updateData);
      commit('UPDATE_OPPORTUNITY', data);
      return data;
    } catch (error) {
      throw error;
    }
  },

  async moveStage({ commit }, { id, pipeline_stage_id, kanban_order }) {
    commit('SET_UI_FLAG', { isMoving: true });
    // Optimistic update
    commit('MOVE_OPPORTUNITY_STAGE', { id, pipeline_stage_id, kanban_order });
    try {
      await OpportunitiesAPI.moveStage(id, {
        pipeline_stage_id,
        kanban_order,
      });
    } catch (error) {
      // TODO: revert optimistic update on error
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isMoving: false });
    }
  },

  async delete({ commit }, id) {
    try {
      await OpportunitiesAPI.delete(id);
      commit('DELETE_OPPORTUNITY', id);
    } catch (error) {
      throw error;
    }
  },

  async fetchOne({ commit }, id) {
    commit('SET_UI_FLAG', { isFetchingDetail: true });
    try {
      const { data } = await OpportunitiesAPI.show(id);
      commit('SET_CURRENT_OPPORTUNITY', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetchingDetail: false });
    }
  },

  async createActivity({ commit }, { opportunityId, activityData }) {
    commit('SET_UI_FLAG', { isCreatingActivity: true });
    try {
      const { data } = await OpportunitiesAPI.createActivity(
        opportunityId,
        activityData
      );
      commit('ADD_ACTIVITY', data);
      return data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isCreatingActivity: false });
    }
  },

  async deleteActivity({ commit }, { opportunityId, activityId }) {
    try {
      await OpportunitiesAPI.deleteActivity(opportunityId, activityId);
      commit('REMOVE_ACTIVITY', activityId);
    } catch (error) {
      throw error;
    }
  },
};

export default { namespaced: true, state, getters, mutations, actions };
